import { ApiState, IApiState } from '../../shared/models/api-state.model';
import { ActionReducer, createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { ITag } from '../../shared/interfaces/tags.interface';
import {
  AGENTS_API_ACTIONS,
  APPOINTMENT_DETAILS_API_ACTIONS,
  CAMPAIGN_DETAILS_API_ACTIONS,
  CONTACT_DETAILS_API_ACTIONS,
  CONVERSATION_DETAILS_API_ACTIONS,
  LEASING_PREFERENCES_API_ACTIONS,
  TAGS_API_ACTIONS,
} from '../actions';
import { ConversationDetailsModel, IConversationDetails } from '../../shared/models/conversation-details.model';
import { IContactDetails } from '../../shared/models/contact-details.model';
import { FullNamePipe } from '../../shared/pipes/full-name.pipe';
import { ILeasingPreferences } from '../../shared/models/leasing-preferences.model';
import { IAppointmentDetails } from '../../shared/models/appointment-details.model';
import { ICampaignDetails } from '../../shared/models/campaign-details.model';

export const CONVERSATION_DATA_STATE_KEY = 'conversationData';

export interface IConversationDataState {
  tags: IApiState<ITag[]>;
  conversationDetails: IApiState<IConversationDetails>;
  contactDetails: IApiState<IContactDetails>;
  leasingPreferences: IApiState<ILeasingPreferences>;
  appointmentDetails: IApiState<IAppointmentDetails>;
  campaignDetails: IApiState<ICampaignDetails>;
}

export const initialState: IConversationDataState = {
  tags: new ApiState(),
  conversationDetails: new ApiState(),
  contactDetails: new ApiState(),
  leasingPreferences: new ApiState(),
  appointmentDetails: new ApiState(),
  campaignDetails: new ApiState(),
};

const reducer: ActionReducer<IConversationDataState> = createReducer<IConversationDataState>(
  initialState,
  on(
    TAGS_API_ACTIONS.get,
    (state): IConversationDataState => ({
      ...state,
      tags: state.tags.load(),
    }),
  ),
  on(
    TAGS_API_ACTIONS.error,
    (state, { error }): IConversationDataState => ({
      ...state,
      tags: state.tags.setError(error),
    }),
  ),
  on(
    TAGS_API_ACTIONS.success,
    (state, { data }): IConversationDataState => ({
      ...state,
      tags: state.tags.success(data.tags),
    }),
  ),
  on(
    CONVERSATION_DETAILS_API_ACTIONS.get,
    (state): IConversationDataState => ({
      ...state,
      conversationDetails: state.conversationDetails.load(),
    }),
  ),
  on(
    CONVERSATION_DETAILS_API_ACTIONS.error,
    (state, { error }): IConversationDataState => ({
      ...state,
      conversationDetails: state.conversationDetails.setError(error),
    }),
  ),
  on(
    CONVERSATION_DETAILS_API_ACTIONS.success,
    (state, { data }): IConversationDataState => ({
      ...state,
      conversationDetails: state.conversationDetails.success(data),
    }),
  ),
  on(
    CONTACT_DETAILS_API_ACTIONS.get,
    (state): IConversationDataState => ({
      ...state,
      contactDetails: state.contactDetails.load(),
    }),
  ),
  on(
    CONTACT_DETAILS_API_ACTIONS.error,
    (state, { error }): IConversationDataState => ({
      ...state,
      contactDetails: state.contactDetails.setError(error),
    }),
  ),
  on(
    CONTACT_DETAILS_API_ACTIONS.success,
    (state, { data }): IConversationDataState => ({
      ...state,
      contactDetails: state.contactDetails.success(data),
    }),
  ),
  on(
    LEASING_PREFERENCES_API_ACTIONS.get,
    (state): IConversationDataState => ({
      ...state,
      leasingPreferences: state.leasingPreferences.load(),
    }),
  ),
  on(
    LEASING_PREFERENCES_API_ACTIONS.error,
    (state, { error }): IConversationDataState => ({
      ...state,
      leasingPreferences: state.leasingPreferences.setError(error),
    }),
  ),
  on(
    LEASING_PREFERENCES_API_ACTIONS.success,
    (state, { data }): IConversationDataState => ({
      ...state,
      leasingPreferences: state.leasingPreferences.success(data),
    }),
  ),
  on(
    APPOINTMENT_DETAILS_API_ACTIONS.get,
    (state): IConversationDataState => ({
      ...state,
      appointmentDetails: state.appointmentDetails.load(),
    }),
  ),
  on(
    APPOINTMENT_DETAILS_API_ACTIONS.error,
    (state, { error }): IConversationDataState => ({
      ...state,
      appointmentDetails: state.appointmentDetails.setError(error),
    }),
  ),
  on(
    APPOINTMENT_DETAILS_API_ACTIONS.success,
    (state, { data }): IConversationDataState => ({
      ...state,
      appointmentDetails: state.appointmentDetails.success(data),
    }),
  ),
  on(
    CAMPAIGN_DETAILS_API_ACTIONS.get,
    (state): IConversationDataState => ({
      ...state,
      campaignDetails: state.campaignDetails.load(),
    }),
  ),
  on(
    CAMPAIGN_DETAILS_API_ACTIONS.error,
    (state, { error }): IConversationDataState => ({
      ...state,
      campaignDetails: state.campaignDetails.setError(error),
    }),
  ),
  on(
    CAMPAIGN_DETAILS_API_ACTIONS.success,
    (state, { data }): IConversationDataState => ({
      ...state,
      campaignDetails: state.campaignDetails.success(data),
    }),
  ),

  on(
    AGENTS_API_ACTIONS.updateSuccess,
    (state, { firstName: agentFirstName, lastName: agentLastName }): IConversationDataState => ({
      ...state,
      conversationDetails: state.conversationDetails.success(
        new ConversationDetailsModel({
          ...state.conversationDetails.data,
          agentFirstName,
          agentLastName,
        }),
      ),
    }),
  ),
);

export const CONVERSATION_DATA_REDUCER = createFeature({
  name: CONVERSATION_DATA_STATE_KEY,
  reducer,
  extraSelectors: ({ selectContactDetails, selectConversationDetails }) => ({
    userName: createSelector(selectContactDetails, ({ data }): string => {
      const userName = FullNamePipe.getFullName(data?.firstName, data?.lastName);
      return userName ? userName : 'User';
    }),
    siteId: createSelector(selectConversationDetails, ({ data }) => data?.siteId),
  }),
});
